import {Suspense, useEffect, useState} from "react"
import {LoadingPage} from "@indebted/components/Loading"
import {ErrorPage} from "@indebted/components/Error"
import {useNavigate} from "react-router-dom"
import {ErrorBoundary} from "react-error-boundary"
import {useSuspenseQueries} from "@tanstack/react-query"
import {Trans, useTranslation} from "react-i18next"
import {PageTitle, PageSubtitle, NewLayoutPage} from "@indebted/components/Page"
import {Box} from "@mui/material"
import {styled} from "@mui/material/styles"
import {PaymentFormResponse, usePaymentFormPageQuery} from "@indebted/api/paymentArrangement/paymentFormPageAPI"
import {useAnalytics} from "@indebted/analytics"
import {addMonths} from "date-fns"
import {paymentArrangementAPI} from "@indebted/api/paymentArrangementAPI"

import {nextPage} from "../Navigator"
import {formatCurrency} from "../../../src/currencyFormatter"
import {formatDate} from "../../../src/dateFormatter"
import {Breadcrumbs} from "../Breadcrumbs"
import {ValidLocale} from "../../../src/Locales"
import {usePaymentArrangementId} from "../usePaymentArrangementId"
import {useCustomerInfoQuery} from "../../../src/useCustomerInfo"
import {configurePaymentMethodOnSuccess} from "../utils/configure-payment-method"
import {getMaxDate} from "../Slider/Frequency"

import {PaymentForm} from "./PaymentForm"

export function PaymentInFullPage() {
	return (
		<ErrorBoundary
			fallbackRender={({error}) => {
				return <ErrorPage message={error.Message || error.message} error={error} />
			}}
		>
			<Suspense fallback={<LoadingPage />}>
				<PaymentInFullPageLoader />
			</Suspense>
		</ErrorBoundary>
	)
}

function PaymentInFullPageLoader() {
	const paymentArrangementID = usePaymentArrangementId()
	const navigate = useNavigate()

	const customerQuery = useCustomerInfoQuery()
	const paymentFormPageQuery = usePaymentFormPageQuery(paymentArrangementID)
	const [customerInfo, paymentFormPage] = useSuspenseQueries({
		queries: [customerQuery, paymentFormPageQuery],
	})

	useEffect(() => {
		if (paymentFormPage.data.type == "redirect") {
			navigate(nextPage(paymentFormPage.data.Next, paymentArrangementID))
		}
	}, [paymentFormPage.data, navigate, paymentArrangementID])

	if (paymentFormPage.data.type == "redirect") {
		return <LoadingPage />
	}

	return (
		<Form
			{...paymentFormPage.data}
			SecureCode={customerInfo.data.SecureCode}
			CustomerID={customerInfo.data.CustomerID}
			ClientName={customerInfo.data.ClientName}
		/>
	)
}

function Form({
	PaymentArrangementID,
	ClientSecret,
	ProviderSetupID,
	CustomerCountryISOAlpha,
	PaymentArrangementInstalmentAmount,
	PaymentArrangementInstalmentCurrency,
	PaymentArrangementFrequency,
	PaymentArrangementNumberOfInstalments,
	RequestESIGNConsent,
	RequiresEFTADisclosure,
	SecureCode,
	CustomerID,
	PublishableKey,
	EFTAPage,
	ClientName,
}: PaymentFormResponse & {SecureCode: string; CustomerID: string; ClientName: string}) {
	const {t, i18n} = useTranslation()
	const navigate = useNavigate()
	const {revenue} = useAnalytics()
	const [startDate, setStartDate] = useState<Date>(new Date())
	const [isLoading, setIsLoading] = useState(false)

	useEffect(() => {
		const setup = async () => {
			setIsLoading(true)
			await paymentArrangementAPI.postSetup({
				paymentArrangementID: PaymentArrangementID,
				frequency: "Monthly",
				startDate: startDate,
				numberOfPayments: 1,
			})
			setIsLoading(false)
		}
		setup()
	}, [PaymentArrangementID, startDate])

	return (
		<NewLayoutPage>
			<PrimaryContainer>
				<Box sx={{marginBottom: "1rem"}}>
					<Breadcrumbs currentStep={t("breadcrumbs.payment_in_full")} />
				</Box>
				<PageTitle>{t("payment_arrangement_payment.payment_in_full.header")}</PageTitle>
				<PageSubtitle>
					<Trans
						i18nKey={`payment_arrangement_payment.payment_in_full.subtitle`}
						values={{
							ClientName: ClientName,
							OutstandingBalance: formatCurrency(
								PaymentArrangementInstalmentAmount / 100,
								PaymentArrangementInstalmentCurrency,
								i18n.language as ValidLocale,
							),
						}}
						components={{bold: <strong />}}
					/>
				</PageSubtitle>

				<PaymentForm
					ClientSecret={ClientSecret}
					CustomerCountryISOAlpha={CustomerCountryISOAlpha}
					RequestESIGNConsent={RequestESIGNConsent}
					RequiresEFTADisclosure={RequiresEFTADisclosure}
					PublishableKey={PublishableKey}
					SecureCode={SecureCode}
					CustomerID={CustomerID}
					EFTAPage={nextPage(EFTAPage, PaymentArrangementID)}
					ShowDatePicker={true}
					date={startDate}
					maxDate={getMaxDate("Fortnightly")}
					DisableButton={isLoading}
					onDateChange={async (date) => {
						setStartDate(date)
					}}
					showStripeTerms={false}
					ButtonLabel={t("payment_arrangement_payment.payment_in_full.button_label")}
					onSuccess={async (response) => {
						const next = await configurePaymentMethodOnSuccess({
							PaymentMethodID: response.PaymentMethodID,
							ProviderRawData: response.ProviderRawData,
							ProviderSetupID: ProviderSetupID,
							PaymentArrangementID: PaymentArrangementID,
							navigate,
						})

						revenue(
							PaymentArrangementInstalmentAmount,
							PaymentArrangementInstalmentCurrency,
							"PaymentPlan",
							{
								frequency: PaymentArrangementFrequency,
								instalments: PaymentArrangementNumberOfInstalments,
							},
						)

						navigate(nextPage(next, PaymentArrangementID))
					}}
				/>
			</PrimaryContainer>
		</NewLayoutPage>
	)
}

const PrimaryContainer = styled("div")`
	flex-direction: column;
	display: flex;
`
